import axiosInstance from 'shared/services/api/axiosInstance';
import * as analyticsApi from 'features/analytics/studio/api/analyticsApi';
import { LEGACY_STUDIO_UI } from 'shared/config/featureFlags';

const INSIGHTS_EVENTS_PRELOAD_V = 1;
const INSIGHTS_EVENTS_PREFIX = 'trodo_insights_events_preload';
const EVENTS_PRELOAD_TTL_MS = 45 * 60 * 1000;

function insightsEventsCacheKey(teamMongoId) {
  try {
    const raw = localStorage.getItem('User');
    if (!raw) return null;
    const u = JSON.parse(raw);
    const uid = u.id != null && u.id !== '' ? String(u.id) : null;
    if (!uid || teamMongoId == null || teamMongoId === '') return null;
    return `${INSIGHTS_EVENTS_PREFIX}_v${INSIGHTS_EVENTS_PRELOAD_V}:${uid}:${String(teamMongoId)}`;
  } catch {
    return null;
  }
}

/**
 * Session cache of /insights/events for chart builders (useEventsMetadata).
 * Populated during login / team switch warm-up.
 */
export function readInsightsEventsPreload(teamMongoId) {
  const key = insightsEventsCacheKey(teamMongoId);
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== INSIGHTS_EVENTS_PRELOAD_V) return null;
    if (typeof parsed.savedAt !== 'number' || Date.now() - parsed.savedAt > EVENTS_PRELOAD_TTL_MS) {
      sessionStorage.removeItem(key);
      return null;
    }
    return { data: parsed.payload };
  } catch {
    return null;
  }
}

function writeInsightsEventsPreload(teamMongoId, apiPayload) {
  const key = insightsEventsCacheKey(teamMongoId);
  if (!key) return;
  try {
    sessionStorage.setItem(
      key,
      JSON.stringify({
        v: INSIGHTS_EVENTS_PRELOAD_V,
        savedAt: Date.now(),
        payload: apiPayload,
      })
    );
  } catch {
    /* quota */
  }
}

const AGENT_EVENTS_PRELOAD_V = 1;
const AGENT_EVENTS_PREFIX = 'trodo_insights_agent_events_preload';

function agentEventsCacheKey(teamMongoId) {
  try {
    const raw = localStorage.getItem('User');
    if (!raw) return null;
    const u = JSON.parse(raw);
    const uid = u.id != null && u.id !== '' ? String(u.id) : null;
    if (!uid || teamMongoId == null || teamMongoId === '') return null;
    return `${AGENT_EVENTS_PREFIX}_v${AGENT_EVENTS_PRELOAD_V}:${uid}:${String(teamMongoId)}`;
  } catch {
    return null;
  }
}

/**
 * Session cache for /insights/agent-events (all agent tabs). Used by warm-up + query UI.
 */
export function readAgentEventsPreload(teamMongoId) {
  const key = agentEventsCacheKey(teamMongoId);
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== AGENT_EVENTS_PRELOAD_V) return null;
    if (typeof parsed.savedAt !== 'number' || Date.now() - parsed.savedAt > EVENTS_PRELOAD_TTL_MS) {
      sessionStorage.removeItem(key);
      return null;
    }
    return parsed.payload && typeof parsed.payload === 'object' ? parsed.payload : null;
  } catch {
    return null;
  }
}

async function warmAgentEventsCaches(teamMongoId) {
  if (!teamMongoId) return;
  const key = agentEventsCacheKey(teamMongoId);
  if (!key) return;
  const types = [
    ['agents', 'agent_calls'],
    ['agent_tools', 'agent_tools'],
    ['agent_errors', 'agent_errors'],
  ];
  const payload = {};
  await Promise.all(
    types.map(async ([queryType, storageKey]) => {
      try {
        const res = await axiosInstance.get(`/insights/agent-events?type=${queryType}`);
        payload[storageKey] = res.data;
      } catch (e) {
        console.error('Failed to preload agent events:', queryType, e);
        payload[storageKey] = { data: [] };
      }
    })
  );
  try {
    sessionStorage.setItem(
      key,
      JSON.stringify({
        v: AGENT_EVENTS_PRELOAD_V,
        savedAt: Date.now(),
        payload,
      })
    );
  } catch {
    /* quota */
  }
}

/**
 * True when the website list is cached — the only thing the gate now requires.
 * All analytics data (events, agent events, property summaries) warms in the
 * background via warmAnalyticsCaches() and does not block the spinner.
 */
export function areWorkspaceCachesReady(teamMongoId) {
  if (teamMongoId == null || teamMongoId === '') return true;
  try {
    if (sessionStorage.getItem('preloadedWebsites') == null) return false;
  } catch {
    return false;
  }
  return true;
}

function getSelectedTeamMongoId() {
  try {
    const raw = localStorage.getItem('selectedTeamData');
    if (!raw) return null;
    const t = JSON.parse(raw);
    return t?._id || t?.id || null;
  } catch {
    return null;
  }
}

/**
 * Run speculative work once the browser is idle.
 *
 * Fire-and-forget is not the same as free. These warms are speculative — they
 * fill caches for popovers and pages you may never open — but they issue ~10
 * requests, and a browser allows about six per origin. Started alongside a
 * page's own fetches they take most of that budget, so the data actually on
 * screen queues behind them: the spans histogram measured 2458ms during page
 * load against 214-630ms called on its own. Nothing was slow; it was waiting.
 *
 * requestIdleCallback yields until the real work has gone out. The timeout is
 * the backstop for a tab that never idles (and for Safari, which still lacks
 * the API) so the warm degrades to "a bit later" rather than "never".
 */
function onIdle(fn, timeout = 2000) {
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(fn, { timeout });
  } else {
    setTimeout(fn, 300);
  }
}

/**
 * Kicks off background cache warming for analytics data.
 * Nothing here blocks the spinner — all tasks are fire-and-forget, and each
 * waits for the browser to go idle so it never competes with the page's own
 * requests for the per-origin connection budget.
 */
export function warmAnalyticsCaches(teamMongoId) {
  if (!teamMongoId) return;

  // The studio warms: the events list only the insights editor reads, and the
  // agent-events catalog, which nothing reads at all any more. With the studio
  // hidden (TRO-466) they were requests on every app start for pages nobody
  // can open.
  if (LEGACY_STUDIO_UI) warmStudioCaches(teamMongoId);

  // There used to be a third warm here: the filter property catalog for the
  // team plus one per agent (up to ten requests, each a set of key-discovery
  // scans), run on every login and team switch for a popover most sessions
  // never open. The picker now loads on demand — fixed properties on first
  // open, the rest page by page — so there is nothing to warm.
}

function warmStudioCaches(teamMongoId) {
  onIdle(() => {
    void (async () => {
      try {
        const eventsPayload = await analyticsApi.fetchEvents(teamMongoId);
        writeInsightsEventsPreload(teamMongoId, eventsPayload);
      } catch (e) {
        console.error('Failed to preload insights events list:', e);
      }
    })();
  });

  onIdle(() => {
    void (async () => {
      try {
        await warmAgentEventsCaches(teamMongoId);
      } catch (e) {
        console.error('Failed to warm agent events caches:', e);
      }
    })();
  });
}

/**
 * Preloads important dropdown data when a user logs in
 * This improves the user experience by fetching frequently used data ahead of time
 * @param {boolean} forceRefresh - Whether to force refresh all data even if cached
 * @param {string} teamId - Optional team ID to load data for (defaults to selectedTeam from localStorage)
 */
const preloadData = async (forceRefresh = false, teamId = null) => {
  try {
    const selectedTeam = teamId || localStorage.getItem('selectedTeam');
    if (!selectedTeam) {
      console.log('No team selected, skipping preload');
      return;
    }

    console.log('Preloading website data for team:', selectedTeam);

    const now = Date.now();
    const lastPreloadTime = parseInt(sessionStorage.getItem('lastPreloadTime') || '0', 10);

    if (!forceRefresh && now - lastPreloadTime < 10000) {
      console.log('Preload throttled - too recent. Using existing data.');
      return;
    }

    if (forceRefresh) {
      console.log('Force refresh requested, clearing existing data');
      sessionStorage.removeItem('preloadedWebsites');
    }

    const teamMongoId = getSelectedTeamMongoId();

    const websiteTask = (async () => {
      try {
        await preloadWebsites(selectedTeam);
        console.log('Successfully preloaded websites');
      } catch (e) {
        console.error('Failed to preload websites:', e);
      }
    })();

    const warmTask =
      teamMongoId != null && teamMongoId !== ''
        ? (async () => {
            try {
              await warmAnalyticsCaches(teamMongoId);
              console.log('Analytics caches warmed');
            } catch (e) {
              console.error('Error warming analytics caches:', e);
            }
          })()
        : Promise.resolve();

    await Promise.all([websiteTask, warmTask]);

    sessionStorage.setItem('lastPreloadTime', now.toString());
    console.log('Preloading completed');
  } catch (error) {
    console.error('Error preloading data:', error);
  }
};

/**
 * Preloads website data for the selected team
 */
const preloadWebsites = async (teamId) => {
  try {
    console.log('Preloading websites for team:', teamId);

    const cachedWebsites = sessionStorage.getItem('preloadedWebsites');
    const lastWebsitesRefreshTime = parseInt(sessionStorage.getItem('lastWebsitesRefreshTime') || '0', 10);
    const now = Date.now();

    if (cachedWebsites && now - lastWebsitesRefreshTime < 300000) {
      console.log('Using cached website data (< 5 minutes old)');
      return JSON.parse(cachedWebsites);
    }

    try {
      const is24CharHexTeamKey = typeof teamId === 'string' && /^[a-fA-F0-9]{24}$/.test(teamId);
      const response = is24CharHexTeamKey
        ? await axiosInstance.get(`/website/team/${teamId}`)
        : await axiosInstance.get(`/website/name/${teamId}`);

      if (response.status === 200 && response.data.websites) {
        console.log(`Successfully preloaded ${response.data.websites.length} websites`);

        sessionStorage.setItem('preloadedWebsites', JSON.stringify(response.data.websites));
        sessionStorage.setItem('lastWebsitesRefreshTime', now.toString());

        if (!localStorage.getItem('selectedWebsite') && response.data.websites.length > 0) {
          const firstWebsite = response.data.websites[0];
          localStorage.setItem('idy', firstWebsite.site_id || firstWebsite.siteId);
          localStorage.setItem('selectedWebsite', firstWebsite.domain || firstWebsite.Domain);
        }

        return response.data.websites;
      }
    } catch (apiError) {
      console.error('Error preloading websites:', apiError);

      if (cachedWebsites) {
        console.log('API failed, falling back to cached website data');
        return JSON.parse(cachedWebsites);
      }

      if (apiError.code === 'ECONNABORTED' && apiError.message.includes('timeout')) {
        console.warn('Website preload timed out - continuing without website data');
        return [];
      }

      throw apiError;
    }
  } catch (error) {
    console.error('Error preloading websites:', error);
    return [];
  }
};

export default preloadData;
